/**
 * docx-writer — produce a REAL .docx (OOXML) instead of HTML wearing a Word
 * extension.
 *
 * Why this exists. Every "Download DOCX" button on the site used the old
 * Word-HTML trick: an HTML string shipped as `transcription.doc` with mime
 * `application/msword` (speech-to-text, notepad, text-to-speech), or — worse —
 * HTML shipped under the real OOXML mime (document-writer-export.js). Desktop
 * Word forgives that. Android/iOS Word, Google Docs and WPS largely do not:
 * they see a .doc whose bytes are HTML and refuse or mangle it.
 *
 * A Pro user on Android reported exactly that on 2026-08-01 — "при скачивании
 * вордовского документа, он не открывается" — after the button had promised
 * DOCX. The file was never a DOCX.
 *
 * A minimal valid .docx is a ZIP holding four parts:
 *   [Content_Types].xml   — declares the part types
 *   _rels/.rels           — points at the main document
 *   word/document.xml     — the content
 *   docProps/core.xml     — title/created (optional, but Word shows it)
 *
 * JSZip is already vendored and lazy-loaded by several tools; we follow the
 * same pattern so nobody pays for the library unless they click Download.
 *
 * Usage:
 *   const blob = await window.DocxWriter.build({
 *       title: 'Расшифровка',
 *       subtitle: '01.08.2026, 14:32',      // optional grey line under the title
 *       paragraphs: ['первый абзац', 'второй'],
 *   });
 *   window.__downloadFile(blob, 'transcription.docx');
 *
 * A laid-out document — a court application, a letter, a calculation — is a
 * list of `blocks` instead of `paragraphs` (first user: /debt-indexation, the
 * application a lawyer files and therefore edits in Word, 06.10.2026):
 *   DocxWriter.build({
 *       docTitle: 'Заявление об индексации',          // file properties only
 *       font: 'Times New Roman', size: 24,            // half-points
 *       line: 360, lang: 'ru-RU',                     // 1.5 line spacing
 *       blocks: [
 *           { text: 'В Арбитражный суд …', indentLeft: 4962 },
 *           { text: 'ЗАЯВЛЕНИЕ', align: 'center' },
 *           { text: 'Решением суда …', align: 'both', firstLine: 567 },
 *           { runs: [{ text: 'ПРОШУ:', bold: true }], align: 'center' },
 *           { table: { widths: [2600, 2000], head: ['Период', 'Сумма'],
 *                      rows: [['01.01–31.01', '100,00']],
 *                      foot: [{ text: 'Итого', span: 1 }, '100,00'],
 *                      align: ['left', 'right'], size: 22 } },
 *           { pageBreak: true },
 *       ],
 *   });
 * A table with `borders: false` is a layout grid without lines (the parties'
 * signature columns of a contract, /consent-pd and its siblings, 21.10.2026),
 * and a cell may hold several paragraphs: `{ paras: [{ runs }, { text }] }`.
 * The base font, size, spacing and language go into a styles part, so what a
 * person types into the file later looks like the text around it. Widths and
 * indents are in twips (567 = 1 cm). Without `blocks` the output is exactly
 * what it was before — the transcript callers are untouched.
 *
 * Right-to-left (22.10.2026, /risk-assessment-generator in Arabic): a block or
 * a cell with `dir: 'rtl'` becomes a bidi paragraph (<w:bidi/>, runs <w:rtl/>)
 * — without it Word lays an Arabic paragraph out left to right and the full
 * stop lands on the wrong end. Its alignment is the paragraph's start, so a
 * 'left'/'right' align is dropped there (Word and LibreOffice read those two
 * differently inside a bidi paragraph); 'center'/'both' stay. A table with
 * `rtl: true` runs its columns right to left (<w:bidiVisual/>); per-cell
 * direction — `cell.dir`, else `table.dirs[col]`, else `table.dir`.
 *
 * Pictures (27.10.2026, /invoice-generator — the payment QR, the logo, a stamp
 * and a signature in the Word copy of an invoice): a block or a cell paragraph
 * with `image: {data: Uint8Array, type: 'png'|'jpeg', wMm, hMm}` becomes an
 * inline picture (word/media/imageN, a relationship, a Default content type).
 * A paragraph with `text` and `image` shows the text, then the picture. Without
 * any `image` the package is byte-for-byte what it was.
 */
(function (global) {
    'use strict';

    var JSZIP_URL = '/vendor/jszip/jszip.min.js';
    var MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

    function loadJSZip() {
        if (global.JSZip) return Promise.resolve(global.JSZip);
        return new Promise(function (resolve, reject) {
            var s = document.createElement('script');
            s.src = JSZIP_URL;
            s.onload = function () {
                global.JSZip ? resolve(global.JSZip) : reject(new Error('jszip_absent'));
            };
            s.onerror = function () { reject(new Error('jszip_load_failed')); };
            document.head.appendChild(s);
        });
    }

    /* XML text escape. The five predefined entities plus a scrub of control
       characters — a stray 0x0B in a transcript makes Word declare the whole
       file corrupt, which is precisely the failure we are removing. */
    function xml(s) {
        return String(s == null ? '' : s)
            .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&apos;');
    }

    /* One <w:p>. `opts.bold`, `opts.size` (half-points), `opts.color` (hex,
       no #). A line break inside the text becomes a real <w:br/> so a
       multi-line paragraph survives as one paragraph, the way it looks on
       screen. xml:space="preserve" keeps leading/trailing spaces. */
    function para(text, opts) {
        opts = opts || {};
        var rPr = '';
        if (opts.bold || opts.size || opts.color) {
            rPr = '<w:rPr>'
                + (opts.bold ? '<w:b/>' : '')
                + (opts.size ? '<w:sz w:val="' + opts.size + '"/><w:szCs w:val="' + opts.size + '"/>' : '')
                + (opts.color ? '<w:color w:val="' + opts.color + '"/>' : '')
                + '</w:rPr>';
        }
        var pPr = opts.spaceAfter === false ? '' : '<w:pPr><w:spacing w:after="160"/></w:pPr>';
        var lines = String(text == null ? '' : text).split('\n');
        var runs = '';
        for (var i = 0; i < lines.length; i++) {
            if (i > 0) runs += '<w:r>' + rPr + '<w:br/></w:r>';
            runs += '<w:r>' + rPr + '<w:t xml:space="preserve">' + xml(lines[i]) + '</w:t></w:r>';
        }
        return '<w:p>' + pPr + runs + '</w:p>';
    }

    function documentXml(opts) {
        var body = '';
        if (opts.title) body += para(opts.title, { bold: true, size: 32 });
        if (opts.subtitle) body += para(opts.subtitle, { size: 18, color: '808080' });
        var ps = opts.paragraphs || [];
        for (var i = 0; i < ps.length; i++) {
            var p = ps[i];
            if (p && typeof p === 'object') body += para(p.text, p);
            else if (String(p == null ? '' : p).trim() !== '') body += para(p);
        }
        if (!body) body = para('');
        return '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
            + '<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">'
            + '<w:body>' + body
            + '<w:sectPr><w:pgSz w:w="11906" w:h="16838"/>'
            + '<w:pgMar w:top="1134" w:right="850" w:bottom="1134" w:left="1134"/></w:sectPr>'
            + '</w:body></w:document>';
    }

    /* ── Laid-out documents: blocks ─────────────────────────────────────── */

    var W_NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
    var JC = { left: 'left', center: 'center', right: 'right', both: 'both', justify: 'both' };

    function int(v) { v = Math.round(+v); return isFinite(v) ? v : 0; }

    /* Run properties in schema order (b, i, color, sz, u): Word forgives a
       wrong order, the stricter readers — Pages, some Android viewers — may not. */
    function rPrXml(o) {
        if (!o || !(o.bold || o.italic || o.underline || o.size || o.color || o.rtl)) return '';
        return '<w:rPr>'
            + (o.bold ? '<w:b/><w:bCs/>' : '')
            + (o.italic ? '<w:i/><w:iCs/>' : '')
            + (o.color ? '<w:color w:val="' + xml(o.color) + '"/>' : '')
            + (o.size ? '<w:sz w:val="' + int(o.size) + '"/><w:szCs w:val="' + int(o.size) + '"/>' : '')
            + (o.underline ? '<w:u w:val="single"/>' : '')
            + (o.rtl ? '<w:rtl/>' : '')
            + '</w:rPr>';
    }

    function runXml(text, o) {
        var rPr = rPrXml(o);
        var lines = String(text == null ? '' : text).split('\n');
        var out = '';
        for (var i = 0; i < lines.length; i++) {
            if (i > 0) out += '<w:r>' + rPr + '<w:br/></w:r>';
            out += '<w:r>' + rPr + '<w:t xml:space="preserve">' + xml(lines[i]) + '</w:t></w:r>';
        }
        return out;
    }

    /* Paragraph properties in schema order: keepNext, pageBreakBefore, bidi,
       spacing, ind, jc. */
    function pPrXml(b) {
        var s = '';
        var rtl = b.dir === 'rtl';
        if (b.keepNext) s += '<w:keepNext/>';
        if (b.pageBreakBefore) s += '<w:pageBreakBefore/>';
        if (rtl) s += '<w:bidi/>';
        if (b.spaceBefore != null || b.spaceAfter != null || b.line != null) {
            s += '<w:spacing'
                + (b.spaceBefore != null ? ' w:before="' + int(b.spaceBefore) + '"' : '')
                + (b.spaceAfter != null ? ' w:after="' + int(b.spaceAfter) + '"' : '')
                + (b.line != null ? ' w:line="' + int(b.line) + '" w:lineRule="auto"' : '')
                + '/>';
        }
        if (b.indentLeft || b.firstLine) {
            s += '<w:ind'
                + (b.indentLeft ? ' w:left="' + int(b.indentLeft) + '"' : '')
                + (b.firstLine ? ' w:firstLine="' + int(b.firstLine) + '"' : '')
                + '/>';
        }
        if (b.align && JC[b.align] && !(rtl && (b.align === 'left' || b.align === 'right'))) {
            s += '<w:jc w:val="' + JC[b.align] + '"/>';
        }
        return s ? '<w:pPr>' + s + '</w:pPr>' : '';
    }

    /* Pictures of the document being built (blocksDocumentXml fills, parts() packs). */
    var MEDIA = [];
    var EMU_MM = 36000;
    function imageRun(img) {
        if (!img || !img.data || !img.data.length) return '';
        var n = MEDIA.length + 1;
        var ext = img.type === 'jpeg' || img.type === 'jpg' ? 'jpeg' : 'png';
        MEDIA.push({ name: 'image' + n + '.' + ext, data: img.data, ext: ext });
        var cx = int((+img.wMm || 30) * EMU_MM), cy = int((+img.hMm || 30) * EMU_MM);
        var A = 'http://schemas.openxmlformats.org/drawingml/2006/main';
        var PIC = 'http://schemas.openxmlformats.org/drawingml/2006/picture';
        return '<w:r><w:drawing><wp:inline distT="0" distB="0" distL="0" distR="0">'
            + '<wp:extent cx="' + cx + '" cy="' + cy + '"/><wp:docPr id="' + n + '" name="Picture ' + n + '"/>'
            + '<wp:cNvGraphicFramePr><a:graphicFrameLocks xmlns:a="' + A + '" noChangeAspect="1"/></wp:cNvGraphicFramePr>'
            + '<a:graphic xmlns:a="' + A + '"><a:graphicData uri="' + PIC + '"><pic:pic xmlns:pic="' + PIC + '">'
            + '<pic:nvPicPr><pic:cNvPr id="' + n + '" name="image' + n + '"/><pic:cNvPicPr/></pic:nvPicPr>'
            + '<pic:blipFill><a:blip r:embed="rIdImg' + n + '"/><a:stretch><a:fillRect/></a:stretch></pic:blipFill>'
            + '<pic:spPr><a:xfrm><a:off x="0" y="0"/><a:ext cx="' + cx + '" cy="' + cy + '"/></a:xfrm><a:prstGeom prst="rect"><a:avLst/></a:prstGeom></pic:spPr>'
            + '</pic:pic></a:graphicData></a:graphic></wp:inline></w:drawing></w:r>';
    }

    function paraBlock(b) {
        var runs = '';
        var rtl = b.dir === 'rtl';
        if (b.runs && b.runs.length) {
            for (var i = 0; i < b.runs.length; i++) {
                var r = b.runs[i];
                runs += typeof r === 'string' ? runXml(r, rtl ? { bold: b.bold, italic: b.italic, underline: b.underline, size: b.size, color: b.color, rtl: true } : b) : runXml(r.text, {
                    bold: r.bold != null ? r.bold : b.bold, italic: r.italic != null ? r.italic : b.italic,
                    underline: r.underline, size: r.size || b.size, color: r.color || b.color, rtl: rtl,
                });
            }
        } else {
            runs = b.image && (b.text == null || b.text === '') ? '' : runXml(b.text, rtl ? { bold: b.bold, italic: b.italic, underline: b.underline, size: b.size, color: b.color, rtl: true } : b);
        }
        if (b.image) runs += imageRun(b.image);
        return '<w:p>' + pPrXml(b) + runs + '</w:p>';
    }

    /* A table with a fixed grid: the widths are what the page shows, on every
       reader. A cell is a string or {text, bold, align, span}; a header row
       repeats on each page the table runs onto. */
    function tableBlock(t) {
        var widths = t.widths || [];
        var total = 0, i;
        for (i = 0; i < widths.length; i++) total += int(widths[i]);
        var border = function (side) { return '<w:' + side + ' w:val="single" w:sz="4" w:space="0" w:color="000000"/>'; };
        var out = '<w:tbl><w:tblPr>'
            + (t.rtl ? '<w:bidiVisual/>' : '')
            + '<w:tblW w:w="' + total + '" w:type="dxa"/>'
            // borders:false — раскладка без линий (колонки подписей сторон в договоре)
            + (t.borders === false ? '' : '<w:tblBorders>' + border('top') + border('left') + border('bottom') + border('right') + border('insideH') + border('insideV') + '</w:tblBorders>')
            + '<w:tblLayout w:type="fixed"/>'
            + '<w:tblCellMar><w:left w:w="85" w:type="dxa"/><w:right w:w="85" w:type="dxa"/></w:tblCellMar>'
            + '</w:tblPr><w:tblGrid>';
        for (i = 0; i < widths.length; i++) out += '<w:gridCol w:w="' + int(widths[i]) + '"/>';
        out += '</w:tblGrid>';

        function row(cells, kind) {
            var tr = '<w:tr>' + (kind === 'head' ? '<w:trPr><w:cantSplit/><w:tblHeader/></w:trPr>' : '<w:trPr><w:cantSplit/></w:trPr>');
            var col = 0;
            for (var c = 0; c < cells.length; c++) {
                var cell = cells[c] && typeof cells[c] === 'object' ? cells[c] : { text: cells[c] };
                var span = Math.max(1, int(cell.span || 1));
                var w = 0;
                for (var k = 0; k < span; k++) w += int(widths[col + k] || 0);
                var align = cell.align || (kind === 'head' ? (t.headAlign || 'center') : (t.align && t.align[col]) || 'left');
                tr += '<w:tc><w:tcPr><w:tcW w:w="' + w + '" w:type="dxa"/>'
                    + (span > 1 ? '<w:gridSpan w:val="' + span + '"/>' : '')
                    // valign 'top' — длинный текст в ячейках (пункты договора в протоколе разногласий)
                    + '<w:vAlign w:val="' + (t.valign === 'top' || t.valign === 'bottom' ? t.valign : 'center') + '"/></w:tcPr>'
                    + (cell.paras || [cell]).map(function (pc) {
                        // cell.paras — несколько абзацев в ячейке (роль, должность, линия подписи)
                        return paraBlock({
                            text: pc.text, runs: pc.runs, image: pc.image, align: pc.align || align, size: pc.size || cell.size || t.size,
                            bold: pc.bold != null ? pc.bold : cell.bold != null ? cell.bold : kind !== 'row',
                            spaceBefore: 40, spaceAfter: 40, line: 240,
                            dir: pc.dir || cell.dir || (t.dirs && t.dirs[col]) || t.dir,
                            // «держать со следующим» у строки-раскладки (место и дата над подписью
                            // в /consent-pd): Word не отрывает её от таблицы подписей
                            keepNext: pc.keepNext || cell.keepNext,
                        });
                    }).join('')
                    + '</w:tc>';
                col += span;
            }
            return tr + '</w:tr>';
        }
        if (t.head && t.head.length) out += row(t.head, 'head');
        var rows = t.rows || [];
        for (i = 0; i < rows.length; i++) out += row(rows[i], 'row');
        if (t.foot && t.foot.length) out += row(t.foot, 'foot');
        return out + '</w:tbl>';
    }

    function blocksXml(blocks) {
        var body = '', lastWasTable = false;
        for (var i = 0; i < blocks.length; i++) {
            var b = blocks[i];
            if (b == null) continue;
            if (typeof b === 'string') b = { text: b };
            if (b.table) {
                // Two tables in a row merge into one in Word unless a paragraph parts them.
                if (lastWasTable) body += '<w:p/>';
                body += tableBlock(b.table);
                lastWasTable = true;
                continue;
            }
            body += b.pageBreak ? '<w:p><w:r><w:br w:type="page"/></w:r></w:p>' : paraBlock(b);
            lastWasTable = false;
        }
        // The body may not end on a table: the section properties need a paragraph before them.
        if (!body || lastWasTable) body += '<w:p/>';
        return body;
    }

    function blocksDocumentXml(opts) {
        MEDIA = [];
        return '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
            + '<w:document xmlns:w="' + W_NS + '" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" xmlns:wp="http://schemas.openxmlformats.org/drawingml/2006/wordprocessingDrawing">'
            + '<w:body>' + blocksXml(opts.blocks || [])
            + '<w:sectPr><w:pgSz w:w="11906" w:h="16838"/>'
            + '<w:pgMar w:top="1134" w:right="850" w:bottom="1134" w:left="1134" w:header="720" w:footer="720" w:gutter="0"/></w:sectPr>'
            + '</w:body></w:document>';
    }

    /* The document's defaults: one place for the face, the size, the spacing
       and the language of everything not overridden — including what a person
       types into the file afterwards. */
    function stylesXml(opts) {
        var font = xml(opts.font || 'Times New Roman');
        var size = int(opts.size || 24);
        var lang = xml(opts.lang || 'ru-RU');
        return '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
            + '<w:styles xmlns:w="' + W_NS + '">'
            + '<w:docDefaults><w:rPrDefault><w:rPr>'
            + '<w:rFonts w:ascii="' + font + '" w:hAnsi="' + font + '" w:eastAsia="' + font + '" w:cs="' + font + '"/>'
            + '<w:sz w:val="' + size + '"/><w:szCs w:val="' + size + '"/>'
            + '<w:lang w:val="' + lang + '" w:eastAsia="' + lang + '" w:bidi="ar-SA"/>'
            + '</w:rPr></w:rPrDefault>'
            + '<w:pPrDefault><w:pPr><w:spacing w:after="' + int(opts.spaceAfter || 0) + '" w:line="' + int(opts.line || 240) + '" w:lineRule="auto"/></w:pPr></w:pPrDefault>'
            + '</w:docDefaults>'
            + '<w:style w:type="paragraph" w:default="1" w:styleId="Normal"><w:name w:val="Normal"/><w:qFormat/></w:style>'
            + '<w:style w:type="table" w:default="1" w:styleId="TableNormal"><w:name w:val="Normal Table"/><w:uiPriority w:val="99"/><w:semiHidden/><w:unhideWhenUsed/>'
            + '<w:tblPr><w:tblInd w:w="0" w:type="dxa"/><w:tblCellMar><w:top w:w="0" w:type="dxa"/><w:left w:w="108" w:type="dxa"/><w:bottom w:w="0" w:type="dxa"/><w:right w:w="108" w:type="dxa"/></w:tblCellMar></w:tblPr></w:style>'
            + '</w:styles>';
    }

    var STYLES_OVERRIDE = '<Override PartName="/word/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.styles+xml"/>';
    var DOCUMENT_RELS = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
        + '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">'
        + '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>'
        + '</Relationships>';

    var CONTENT_TYPES = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
        + '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">'
        + '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>'
        + '<Default Extension="xml" ContentType="application/xml"/>'
        + '<Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>'
        + '<Override PartName="/docProps/core.xml" ContentType="application/vnd.openxmlformats-package.core-properties+xml"/>'
        + '</Types>';

    var RELS = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
        + '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">'
        + '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>'
        + '<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/package/2006/relationships/metadata/core-properties" Target="docProps/core.xml"/>'
        + '</Relationships>';

    function coreXml(title, isoDate) {
        return '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
            + '<cp:coreProperties'
            + ' xmlns:cp="http://schemas.openxmlformats.org/package/2006/metadata/core-properties"'
            + ' xmlns:dc="http://purl.org/dc/elements/1.1/"'
            + ' xmlns:dcterms="http://purl.org/dc/terms/"'
            + ' xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">'
            + '<dc:title>' + xml(title || '') + '</dc:title>'
            + '<dcterms:created xsi:type="dcterms:W3CDTF">' + isoDate + '</dcterms:created>'
            + '<dcterms:modified xsi:type="dcterms:W3CDTF">' + isoDate + '</dcterms:modified>'
            + '</cp:coreProperties>';
    }

    /**
     * The parts of the package, by path — what build() zips. Separate so the
     * layout can be checked without a browser or JSZip.
     * @param {{title?:string, subtitle?:string, paragraphs?:Array, blocks?:Array}} opts
     * @returns {Object<string,string>}
     */
    function parts(opts, iso) {
        opts = opts || {};
        if (!opts.blocks) {
            return {
                '[Content_Types].xml': CONTENT_TYPES,
                '_rels/.rels': RELS,
                'docProps/core.xml': coreXml(opts.title, iso),
                'word/document.xml': documentXml(opts),
            };
        }
        var doc = blocksDocumentXml(opts);
        var media = MEDIA;
        var out = {
            '[Content_Types].xml': CONTENT_TYPES.replace('</Types>', STYLES_OVERRIDE + '</Types>'),
            '_rels/.rels': RELS,
            'docProps/core.xml': coreXml(opts.docTitle || opts.title, iso),
            'word/document.xml': doc,
            'word/styles.xml': stylesXml(opts),
            'word/_rels/document.xml.rels': DOCUMENT_RELS,
        };
        if (media.length) {
            var exts = {}, rels = '';
            for (var m = 0; m < media.length; m++) {
                exts[media[m].ext] = 1;
                out['word/media/' + media[m].name] = media[m].data;
                rels += '<Relationship Id="rIdImg' + (m + 1) + '" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/image" Target="media/' + media[m].name + '"/>';
            }
            out['word/_rels/document.xml.rels'] = DOCUMENT_RELS.replace('</Relationships>', rels + '</Relationships>');
            var defaults = Object.keys(exts).map(function (e) { return '<Default Extension="' + e + '" ContentType="image/' + e + '"/>'; }).join('');
            out['[Content_Types].xml'] = out['[Content_Types].xml'].replace('<Default Extension="xml"', defaults + '<Default Extension="xml"');
        }
        return out;
    }

    /**
     * @param {{title?:string, subtitle?:string, paragraphs?:Array, blocks?:Array}} opts
     * @returns {Promise<Blob>} a real .docx
     */
    function build(opts) {
        opts = opts || {};
        var iso = new Date().toISOString().replace(/\.\d{3}Z$/, 'Z');
        return pack(parts(opts, iso));
    }

    /**
     * Zip ready package parts ({path: string|Uint8Array}) into a .docx — for a
     * caller that edits an existing Word package (the comparison of /text-diff
     * writes tracked changes into the revised document's own XML, 28.10.2026)
     * and must not grow an archiver of its own.
     * @returns {Promise<Blob>}
     */
    function pack(files) {
        return loadJSZip().then(function (JSZip) {
            var zip = new JSZip();
            for (var path in files) {
                if (Object.prototype.hasOwnProperty.call(files, path)) zip.file(path, files[path]);
            }
            return zip.generateAsync({
                type: 'blob',
                mimeType: MIME,
                compression: 'DEFLATE',
                compressionOptions: { level: 6 },
            });
        });
    }

    global.DocxWriter = { build: build, pack: pack, MIME: MIME, _xml: xml, _documentXml: documentXml, _parts: parts };
})(typeof window !== 'undefined' ? window : globalThis);
